import React, { useEffect, useState } from 'react';

import ComboBox, { ComboxBoxProps } from 'ds/ComboBox';
import ComboBoxResult from 'ds/ComboBox/ComboBoxResult';
import NoMatchesItem from 'ds/ComboBox/NoMatchesItem';
import Icon from 'ds/Icon';
import Layout from 'ds/Layout';
import { Props as TextInputProps } from 'ds/inputs/TextInput';
import { AdminLandlordRow } from 'shared';

export interface Props
  extends
    Pick<TextInputProps, 'size' | 'placeholder' | 'disabled' | 'required' | 'isReadOnly' | 'icon'>,
    ComboxBoxProps {
  label?: string;
  landlords: AdminLandlordRow[];
  selectedLandlordId?: number | null;
  errorMessage?: string;
  onSelect: (landlord: AdminLandlordRow | undefined) => void;
  clearQueryAfterSelect?: boolean;
  onCreateNew?: (query?: string) => void;
}

const LandlordComboBox: React.FC<Props> = ({
  size,
  landlords,
  label = 'Landlord',
  selectedLandlordId,
  errorMessage,
  onSelect,
  placeholder = 'Search landlords by name or email',
  disabled,
  required = false,
  clearQueryAfterSelect,
  onCreateNew,
  isReadOnly,
  readOnlyHref,
  icon
}) => {
  const formatQuery = (landlord?: AdminLandlordRow) => {
    if (!landlord) return undefined;
    return landlord.name;
  };

  const [query, setQuery] = useState<string | undefined>(formatQuery(landlords.find(l => l.id === selectedLandlordId)));
  const [width, setWidth] = useState<number>(0);

  const selectedLandlord = landlords.find(l => l.id === selectedLandlordId);
  const formattedQuery = selectedLandlord ? formatQuery(selectedLandlord) : undefined;

  useEffect(() => {
    if (!formattedQuery) {
      setQuery(undefined);
      return;
    }
    setQuery(formattedQuery);
  }, [formattedQuery]);

  const resultLandlords = query
    ? landlords.filter(
        landlord =>
          landlord.name.toLowerCase().includes(query.toLowerCase()) ||
          landlord.email?.toLowerCase().includes(query.toLowerCase())
      )
    : landlords;

  const results = resultLandlords.map(landlord => ({
    value: landlord.id,
    element: (
      <ComboBoxResult
        key={landlord.id}
        label={landlord.name}
        subLabel={landlord.email || undefined}
        query={query || ''}
      />
    )
  }));

  return (
    <Layout
      onMeasure={({ width }) => {
        setWidth(width);
      }}
      position="relative"
    >
      <ComboBox
        query={query}
        results={results}
        noMatchesItem={
          !!selectedLandlordId ? undefined : <NoMatchesItem size={size} width={width} isCreatable={!!onCreateNew} />
        }
        error={errorMessage}
        size={size}
        label={label}
        bigLabel={placeholder}
        placeholder={placeholder}
        onChange={({ value }) => {
          setQuery(value);
          onSelect(undefined);
        }}
        onCreateNew={onCreateNew ? () => onCreateNew(query) : undefined}
        onSelect={value => {
          const landlord = landlords.find(l => l.id === value);

          if (!landlord) {
            setQuery(undefined);
            return;
          }

          onSelect(landlord);
          if (clearQueryAfterSelect) {
            setQuery(undefined);
          } else {
            setQuery(landlord.name);
          }
        }}
        onClear={() => {
          setQuery(undefined);
          onSelect(undefined);
        }}
        highlightMatch
        required={required}
        showResultsWhenEmpty
        hideLabel
        maxItems={8}
        isReadOnly={isReadOnly}
        disabled={disabled && !isReadOnly}
      />
      {icon && isReadOnly && readOnlyHref && (
        <div
          style={{
            position: 'absolute',
            right: '12px',
            top: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            pointerEvents: 'all',
            zIndex: 1000
          }}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            window.open(readOnlyHref, '_blank', 'noopener,noreferrer');
          }}
          onMouseDown={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Icon name={icon} size="md" color="gray-500" />
        </div>
      )}
    </Layout>
  );
};

export default LandlordComboBox;
